/* eslint-disable no-restricted-globals */

import * as Sentry from '@sentry/react';
import { v4 as uuidv4 } from 'uuid';
import _union from 'lodash/union';
import { NavigateFunction } from 'react-router-dom';
import {
  storeErrors,
  storeSpecificErrors,
  clearErrors,
  setLoading,
  clearEverything,
  killLoading,
} from './commonActions';
import { AppDispatch, RootState } from '../store';
import { clearNotificationMessage, displayFailMessage } from './notificationActions';
import { saveToken, removeBlockFlags, logoutUserAction } from './authActions';
import { storeMeta } from './metaActions';
import * as sdk from '../sdk';
import { toaster, setLocalItem, getLocalItem } from '../common';
import { showNotificationBanner } from '../reducers/notificationBanners';
import DataLayer from '../utils/dataLayer';
import { localVisitedProfiles, updateVisitedProfiles, localDashboardProfiles } from '../utils/storedLocalData';
import { formatFormData } from '../utils/helpers';
import MasterConfig from '../config/Master';
import { EVIDENTLY_FEATURE_LOCAL_STORAGE_KEYS_BASED_ON_FEATURES } from '../constants/evidently';
import { removeDiscoverModeAccountsByHashId } from './discoverModeActions';
import {
  SET_FACELIVENESS_STATUS,
  SET_FACELIVENESS_VERIFY_LOADER,
  SET_SHOW_SELFIE_VERIFICATION_WALL,
  START_SELFIE_VERIFICATION,
  UPDATE_PROFILE_LIVENESS_STATUS,
  SKIP_SELFIE_VERIFICATION_WALL_CHECK,
  IS_SV_WALL_BY_GET_SELFIE_VERIFIED,
} from './actionType';
import { isEmpty } from 'lodash';
import { ActionReturnType } from './types';
import { setShowSkeletonLoader } from './searchActions';

export type UploadMediaFormDataType = {
  id: string;
  file: string; // base64
  isPrivate: boolean;
  isCropped: boolean;
  fileExtension?: string;
};

declare global {
  interface Window {
    dataLayer?: null;
  }
}

export const getProfileV2 = (payload: any): ActionReturnType => {
  return {
    type: 'STORE_PROFILE_V2',
    payload,
  };
};

export const saveProfileDescription = (payload: any): ActionReturnType => {
  return {
    type: 'SAVE_PROFILE_DESCRIPTION',
    payload,
  };
};

export const saveProfileSeeking = (payload: any): ActionReturnType => {
  return {
    type: 'SAVE_PROFILE_SEEKING',
    payload,
  };
};

export const getProfile = (payload: any): ActionReturnType => {
  return {
    type: 'STORE_PROFILE',
    payload,
  };
};

export const getExternalProfile = (payload: any): ActionReturnType => {
  return {
    type: 'STORE_PROFILE_EXTERNAL',
    payload,
  };
};

export const storeProfileMapFields = (payload: any): ActionReturnType => {
  return {
    type: 'STORE_PROFILE_MAP_FIELDS',
    payload,
  };
};

export const storeReportReasons = (payload: any): ActionReturnType => {
  return {
    type: 'STORE_REPORT_REASONS',
    payload,
  };
};

export const externalProfile404 = (payload: any): ActionReturnType => {
  return {
    type: 'STORE_PROFILE_EXTERNAL_404',
    payload,
  };
};

export const remExternalProfile = (): { type: string } => {
  return {
    type: 'REMOVE_PROFILE_EXTERNAL',
  };
};

export const storeSettings = (payload: any): ActionReturnType => {
  if (payload.evidently) {
    Object.keys(payload.evidently).forEach(feature => {
      const storageKey =
        EVIDENTLY_FEATURE_LOCAL_STORAGE_KEYS_BASED_ON_FEATURES[
          feature as keyof typeof EVIDENTLY_FEATURE_LOCAL_STORAGE_KEYS_BASED_ON_FEATURES
        ];

      if (storageKey) {
        setLocalItem(storageKey, JSON.stringify(payload.evidently[feature]));
      }
    });
  }

  return {
    type: 'STORE_SETTINGS',
    payload,
  };
};

export const storeVideoSettings = (payload: any): ActionReturnType => {
  return {
    type: 'STORE_VIDEO_SETTINGS',
    payload,
  };
};

export const storeVideoInfo = (payload: any): ActionReturnType => {
  return {
    type: 'STORE_VIDEO_INFO',
    payload,
  };
};

export const clearVideoInfo = (): { type: string } => {
  return {
    type: 'CLEAR_VIDEO_INFO',
  };
};

export const storeIceServers = (payload: any): ActionReturnType => {
  return {
    type: 'STORE_ICE_SERVERS',
    payload,
  };
};

export const setIsFetchingProfileList = (payload: any): ActionReturnType => {
  return {
    type: 'SET_IS_FETCHING_LIST',
    payload,
  };
};

export const setDeletedLoader = (payload: any): ActionReturnType => {
  return {
    type: 'SET_DELETED_LOADER',
    payload,
  };
};

export const storeBiometricList = (payload: any): ActionReturnType => {
  return {
    type: 'STORE_BIOMETRIC_LIST',
    payload,
  };
};

export const storeNewBlockedUser = (payload: any): ActionReturnType => {
  return {
    type: 'STORE_NEW_BLOCKED_USER',
    payload,
  };
};

export const storeDismissedUser = (payload: any): ActionReturnType => {
  return {
    type: 'STORE_DISMISSED_PROFILES',
    payload,
  };
};

export const updateDismissedUser = (payload: any): ActionReturnType => {
  return {
    type: 'UPDATE_DISMISSED_PROFILES',
    payload,
  };
};

export const updatedBlockedUsers = (payload: any): ActionReturnType => {
  return {
    type: 'UPDATE_BLOCKED_PROFILES',
    payload,
  };
};

export const invertSetting = (payload: any): ActionReturnType => {
  return {
    type: 'INVERT_SETTING',
    payload,
  };
};

export const invertVideoSetting = (payload: any): ActionReturnType => {
  return {
    type: 'INVERT_VIDEO_SETTING',
    payload,
  };
};

export const storeSinglePhoto = (payload: any): ActionReturnType => {
  return {
    type: 'STORE_SINGLE_PHOTO',
    payload,
  };
};

export const updatePhotoUploadProcess = (payload: any): ActionReturnType => {
  return {
    type: 'UPDATE_PHOTO_UPLOAD_PROCESS',
    payload,
  };
};

export const makeAllAvatarsFalse = (): { type: string } => {
  return {
    type: 'MAKE_ALL_AVATARS_FALSE',
  };
};

export const removePhoto = (photoKey: any): ActionReturnType => {
  return {
    type: 'DELETE_SINGLE_PHOTO',
    payload: photoKey,
  };
};

export const removePhotofromProfile = (photoKey: any): ActionReturnType => {
  return {
    type: 'DELETE_SINGLE_PHOTO_FROM_PROFILE',
    payload: photoKey,
  };
};

export const storeDefaultPhoto = (photoKey: any): ActionReturnType => {
  return {
    type: 'STORE_DEFAULT_PHOTO',
    payload: photoKey,
  };
};

export const storeNewPrivacy = (photoKey: any): ActionReturnType => {
  return {
    type: 'STORE_NEW_PRIVACY',
    payload: photoKey,
  };
};

export const makePremium = (): { type: string } => {
  return {
    type: 'MAKE_PREMIUM',
  };
};

export const changeCallStatus = (payload: any): any => {
  return (dispatch: AppDispatch) => {
    dispatch({
      type: 'CHANGE_CALL_STATUS',
      payload,
    });
  };
};

export const setCloseTooltip = (payload: any): any => {
  return {
    type: 'SET_CLOSE_TOOLTIP',
    payload,
  };
};

export const lessOffersCount = (): any => {
  return {
    type: 'STORE_OFFERS_COUNT',
  };
};

// block a specific user (when viewing their extprofile)
export const toggleBlockInStore = (username: any): any => {
  return (dispatch: AppDispatch) => {
    dispatch({
      type: 'TOGGLE_BLOCK_USER_PROFILE',
      payload: username,
    });

    // remove from search results if available
    dispatch({
      type: 'REMOVE_FROM_SEARCH',
      payload: username,
    });
  };
};
//---------

export const pendingUpload = (isPrivate: any): { type: string; payload: { isPrivate: any } } => {
  return {
    type: 'PENDING_UPLOAD',
    payload: { isPrivate },
  };
};

export const endPendingUpload = (isPrivate: any): { type: string; payload: { isPrivate: any } } => {
  return {
    type: 'END_PENDING_UPLOAD',
    payload: { isPrivate },
  };
};

export const gdprAcknowledged = (): { type: string } => {
  return {
    type: 'GDPR_ACKNOWLEDGED',
  };
};

export const gdprOpenStep2 = (): { type: string } => {
  return {
    type: 'GDPR_OPEN_STEP2',
  };
};

export const storeUserInfo = (payload: any): ActionReturnType => {
  return {
    type: 'STORE_GDPR_USER_INFO',
    payload,
  };
};

export const setProcessing = (): { type: string } => {
  return {
    type: 'IS_PROCESSING',
  };
};

export const setIsUpdatingProfileCover = (payload: boolean): { type: string; payload: boolean } => {
  return {
    type: 'IS_UPDATING_PROFILE_COVER',
    payload,
  };
};

export const setIsUpdatingDescription = (payload: boolean): { type: string; payload: boolean } => {
  return {
    type: 'IS_UPDATING_DESCRIPTION',
    payload,
  };
};

export const setIsUpdatingSeekingDesc = (payload: boolean): { type: string; payload: boolean } => {
  return {
    type: 'IS_UPDATING_SEEKING_DESC',
    payload,
  };
};

export const setGalleryLoading = (): { type: string } => {
  return {
    type: 'SET_GALLERY_FETCHING',
  };
};

export const killGalleryLoading = (): { type: string } => {
  return {
    type: 'KILL_GALLERY_FETCHING',
  };
};

export const updateVideoStatus = (key: any, status: any): { type: string; payload: { key: any; status: any } } => {
  return {
    type: 'UPDATE_VIDEO_STATUS',
    payload: { key, status },
  };
};

export const setDarkMode = (config: {
  desktop?: boolean;
  mobile?: boolean;
}): { type: string; payload: { desktop?: boolean; mobile?: boolean } } => {
  return {
    type: 'SET_DARK_MODE',
    payload: config,
  };
};

export const updateIdVerificationStatus = (payload: any): ActionReturnType => {
  return {
    type: 'UPDATE_IDVERIFICATION',
    payload,
  };
};

export const createProfile = (formData: any): any => {
  return (dispatch: AppDispatch) => {
    return sdk
      .createUserProfile(formData)
      .then(() => {
        // refresh profile now
        dispatch(refreshProfile());
      })
      .catch(error => {
        console.log(error);
      });
  };
};

export const refreshProfile = (): any => {
  // get the logged in users profile
  return (dispatch: AppDispatch) => {
    return sdk
      .getLoggedProfile()
      .then(response => {
        const accountData = response.data.data;

        dispatch(getProfile(accountData));
        dispatch(killGalleryLoading());
        dispatch(updateUnreadMessagesCount());

        return accountData;
      })
      .catch(error => {
        console.log(error);
      });
  };
};

export const getProfileMapFields = (): any => {
  return (dispatch: AppDispatch) => {
    return sdk
      .getProfileMapFields()
      .then(response => {
        const profileMapFields = response.data;

        dispatch(storeProfileMapFields(profileMapFields));
      })
      .catch(error => {
        console.log(error);
      });
  };
};

const setErrorFlow = (error: any, dispatch: any, redirect: any) => {
  dispatch(setLoading());
  // Clears session, adds an error and redirects to /login
  const { data } = error.response;

  dispatch(clearEverything());
  dispatch(storeSpecificErrors('form', data.message));

  setLocalItem('redirectTo', redirect === '/login' ? 'dashboard' : location.search.replace('?redirectTo=', ''));

  window.location.href = '/login';
};

const saveProfileFlow = (response: any, dispatch: AppDispatch, isRefreshProfile = false) => {
  // clear errors
  dispatch(clearErrors());
  // save new profile
  dispatch(getProfileV2(response.data.data));
  if (isRefreshProfile) {
    dispatch(refreshProfile());
  } else {
    dispatch(refreshAccount());
  }
};

export const checkEmailToken = (token: any, username = null, redirect = null, navigate: NavigateFunction): any => {
  return (dispatch: AppDispatch) => {
    if (typeof token === 'undefined') {
      dispatch(clearEverything());
      window.location.href = '/login';
      return null;
    }

    dispatch(setLoading());

    return sdk
      .getSignedLinkToken(token)
      .then(response => {
        // save token
        dispatch(saveToken(response.data));

        // check if user is gdpr from email_token
        if (
          response.data.gdpr_acknowledged !== null &&
          response.data.gdpr_acknowledged !== undefined &&
          !response.data.gdpr_acknowledged
        ) {
          navigate('/privacy-updated');
        }

        // retrieve logged in profile.
        dispatch(refreshProfile()).then(() => {
          // hide loading
          dispatch(killLoading());
          // clear errors
          dispatch(clearErrors());

          dispatch(getProfileSettings());

          dispatch(removeBlockFlags());

          if (username) {
            dispatch(refreshExternalProfile(username));
          }
        });
      })
      .catch(error => {
        setErrorFlow(error, dispatch, redirect);
      });
  };
};

export const checkEmailPromoToken = (token: any, redirect = null): any => {
  return (dispatch: AppDispatch) => {
    if (typeof token === 'undefined') {
      dispatch(clearEverything());
      window.location.href = '/login';
      return null;
    }

    dispatch(setLoading());

    return sdk
      .getSignedEmailPromoToken(token)
      .then(response => {
        dispatch(saveToken(response.data));
        dispatch(setLoading());
        dispatch(clearErrors());
      })
      .catch(error => {
        setErrorFlow(error, dispatch, redirect);
      });
  };
};

export const refreshAccount = (): any => {
  return (dispatch: AppDispatch) => {
    return sdk
      .getLoggedAccount()
      .then(response => {
        const accountData = response.data.data;

        dispatch(getProfile(accountData));
        dispatch(killGalleryLoading());
        return accountData;
      })
      .catch(error => {
        console.log(error);
      });
  };
};

export const buttonEventHandler = (isAccept: any, navigate: NavigateFunction): any => {
  return (dispatch: AppDispatch) => {
    if (isAccept) {
      return dispatch(gdprOpenStep2());
    }
    return navigate('/settings?gdpr=true');
  };
};

export const updateProfileCover = (formData: any): any => {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsUpdatingProfileCover(true));

    return await sdk
      .updateProfileCoverData(formData)
      .then(response => {
        saveProfileFlow(response, dispatch);
        dispatch(setIsUpdatingProfileCover(false));
      })
      .catch(error => {
        dispatch(setIsUpdatingProfileCover(false));
        dispatch(storeErrors(error.response.data));
      });
  };
};

export const updateProfileOpeningOffer = (formData: any): any => {
  return (dispatch: AppDispatch) => {
    dispatch(setProcessing());
    return sdk
      .updateProfileOpeningOfferData(formData)
      .then(response => {
        // hide loading
        dispatch(setProcessing());
        // clear errors
        dispatch(clearErrors());
        // save new profile
        dispatch(getProfileV2(response.data.data));
        dispatch(refreshProfile());
      })
      .catch(error => {
        // hide loading
        dispatch(setProcessing());
        error?.response?.data && dispatch(storeErrors(error.response.data));
      });
  };
};

export const updateProfileAbout = (formData: any): any => {
  return (dispatch: AppDispatch) => {
    dispatch(setProcessing());
    const updatedFormData = formatFormData(formData);

    return sdk
      .updateProfileAboutData(updatedFormData)
      .then(response => {
        // hide loading
        dispatch(setProcessing());
        // clear errors
        dispatch(clearErrors());
        // save new profile
        dispatch(getProfileV2(response.data.data));
        dispatch(refreshProfile());
      })
      .catch(error => {
        // hide loading
        dispatch(setProcessing());
        dispatch(storeErrors(error.response.data));
      });
  };
};

export const updateProfileMandatoryCompletion = (formData: any, callback: any): any => {
  return (dispatch: AppDispatch) => {
    dispatch(setProcessing());
    return sdk
      .updateMandatoryCompletion(formData)
      .then(response => {
        // hide loading
        dispatch(setProcessing());
        // clear errors
        dispatch(clearErrors());
        // save new profile
        dispatch(getProfileV2(response.data.data));
        dispatch(refreshProfile());
        callback();
      })
      .catch(error => {
        // hide loading
        dispatch(setProcessing());
        error?.response?.data && dispatch(storeErrors(error.response.data));
      });
  };
};

export const updateProfileInterest = (formData: any): any => {
  return (dispatch: AppDispatch) => {
    return sdk
      .updateProfileInterestData(formData)
      .then(response => {
        saveProfileFlow(response, dispatch, true);
      })
      .catch(error => {
        dispatch(storeErrors(error.response.data));
      });
  };
};

export const updateProfileDescription = (formData: any): any => {
  return (dispatch: AppDispatch) => {
    dispatch(setIsUpdatingDescription(true));
    return sdk
      .updateProfileDescriptionData(formData)
      .then(response => {
        dispatch(saveProfileDescription(response.data.data));
        dispatch(setIsUpdatingDescription(false));
      })
      .catch(error => {
        toaster.error('Sorry, something went wrong.');
        dispatch(setIsUpdatingDescription(false));
        console.error(error);
      });
  };
};

export const updateProfileSeeking = (formData: any): any => {
  return (dispatch: AppDispatch) => {
    dispatch(setIsUpdatingSeekingDesc(true));
    return sdk
      .updateProfileSeekingData(formData)
      .then(response => {
        dispatch(saveProfileSeeking(response.data.data));
        dispatch(setIsUpdatingSeekingDesc(false));
      })
      .catch(error => {
        toaster.error('Sorry, something went wrong.');
        dispatch(setIsUpdatingSeekingDesc(false));
        console.error(error);
      });
  };
};

export const updateProfile = (
  formData: any,
  needsPhoto = false,
  isMigration = false,
  hashID = '',
  navigate: NavigateFunction
): any => {
  return (dispatch: AppDispatch) => {
    dispatch(setProcessing());
    const updatedFormData = formatFormData(formData);

    return sdk
      .updateLoggedProfile(updatedFormData)
      .then(response => {
        // clear errors
        dispatch(clearErrors());
        // save new profile
        dispatch(getProfile(response.data.data));
        // redirect to dashboard
        if (isMigration || needsPhoto) {
          dispatch(refreshProfile());
          setTimeout(() => {
            dispatch(setProcessing());
            navigate(`/profile/${hashID}?successfulMigration=true`);
          }, 500);
        } else {
          dispatch(setProcessing());
          navigate('/dashboard');
        }
      })
      .catch(error => {
        dispatch(setProcessing());
        dispatch(storeErrors(error.response.data));
      });
  };
};

export const getAccountByUserName = (username: any): any => {
  return (dispatch: AppDispatch) => {
    dispatch(remExternalProfile());

    return sdk
      .getAccountByUserName(username)
      .then(response => {
        return response;
      })
      .catch(() => {
        // could not find this profile.
        dispatch(externalProfile404(username));
      });
  };
};

export const removeExternalProfile = (): any => {
  return (dispatch: AppDispatch) => {
    dispatch(remExternalProfile());
  };
};

export const refreshExternalProfile = (username: string): any => {
  return (dispatch: AppDispatch) => {
    const visitedProfiles = localVisitedProfiles();
    const dashboardProfiles = localDashboardProfiles();
    const availableProfiles: { hash_id: string }[] = _union(visitedProfiles, dashboardProfiles);

    const prevExists = availableProfiles.find(profile => profile.hash_id === username);

    if (prevExists) {
      dispatch(getExternalProfile(prevExists));
    }

    return sdk
      .getExternalProfile(username)
      .then(response => {
        updateVisitedProfiles('add', response.data.data);
        dispatch(setShowSkeletonLoader(false));

        return dispatch(getExternalProfile(response.data.data));
      })
      .catch(() => {
        // could not find this profile.
        dispatch(externalProfile404(username));
      });
  };
};

export const getProfileSettings = (): any => {
  return (dispatch: AppDispatch) => {
    return sdk
      .getAccountSettings()
      .then(response => {
        dispatch(storeSettings(response.data.data));

        return response.data.data;
      })
      .catch(error => {
        console.log(error);
      });
  };
};

export const getVideoCallSettings = (): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    return sdk
      .getVideoChatPreferences(getState().profile.hash_id)
      .then(response => {
        dispatch(storeVideoSettings(response.data.data));
      })
      .catch(error => {
        console.log(error);
      });
  };
};

export const getIceServers = (): any => {
  return (dispatch: AppDispatch) => {
    return sdk
      .getTurnCredentials()
      .then(response => {
        dispatch(storeIceServers(response.data.ice_servers));
      })
      .catch(error => {
        console.log(error);
      });
  };
};

export const getCheckInfoRequest = (): any => {
  return (dispatch: AppDispatch) => {
    return sdk
      .getCheckInfoRequest()
      .then(response => {
        dispatch(storeUserInfo(response.data.data.length === 0 ? null : response.data.data));
      })
      .catch(error => {
        console.log(error);
      });
  };
};

export const requestUserInfo = (): any => {
  return (dispatch: AppDispatch) => {
    return sdk
      .requestUserInfo()
      .then(response => {
        dispatch(storeUserInfo(response.data.data));
      })
      .catch(error => {
        console.log(error);
      });
  };
};

export const getBiometricsList = (): any => {
  // store users that this person has blocked.
  return (dispatch: AppDispatch) => {
    return sdk.getBiometricKeys().then(response => {
      dispatch(storeBiometricList(response.data));
    });
  };
};

export const updateBiometricName = (newName: any, deviceId: any): any => {
  return (dispatch: AppDispatch) => {
    dispatch(clearNotificationMessage());
    return sdk
      .editBiometricName(newName, deviceId)
      .then(response => {
        return response;
      })
      .catch(error => {
        dispatch(
          displayFailMessage({
            info: error.response.data.name[0],
          })
        );

        return error.response;
      });
  };
};

export const updateProfileSetting =
  (updatedField: string): any =>
  (_: any, getState: any) => {
    const settings = getState().settings;

    delete settings.id;
    return sdk.updateAccountSettings({ ...getState().settings, updatedField });
  };

export const updateVideoSetting = (): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    return sdk.updateVideoChatPreferences(getState().profile.hash_id, getState().settings.video_call);
  };
};

export const deleteGDPR = (password: any): any => {
  return (dispatch: AppDispatch) => {
    const currentSubscription = getLocalItem('pushSubscription');

    currentSubscription && sdk.unsubscribeWebPush(currentSubscription);

    return sdk
      .gdprDelete(password)
      .then(() => {
        toaster.success('Your account has been successfully deleted.');
        dispatch(clearErrors());
        dispatch(logoutUserAction(null, false));
      })
      .catch(error => {
        const errorMessage =
          (error?.response?.data?.password?.length > 0 && error?.response?.data?.password[0]) ||
          'Something went wrong to delete the GDPR, Pelase try after sometime.';

        dispatch(storeSpecificErrors('error_password_gdpr', errorMessage));
      });
  };
};

export const notifyEmailUpdate = (notifyEmail: any, navigate: NavigateFunction): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    return sdk.gdprAcknowledged({ notify_email: notifyEmail }).then(() => {
      dispatch(clearErrors());
      dispatch(gdprAcknowledged());

      const isUserFirstTimeLogin = JSON.parse(getLocalItem('user_first_time_login'));

      if (isUserFirstTimeLogin) {
        if (getState()?.profile?.profile?.data?.account_type === 'Generous') {
          navigate('/packages');
        } else {
          navigate('/search');
        }
      } else {
        navigate('/dashboard');
      }

      toaster.success('Updated Settings');
    });
  };
};

export const endImageUploadLoader = (response: any, isPrivate: any): any => {
  return (dispatch: AppDispatch) => {
    // new photo accepted.
    dispatch(endPendingUpload(isPrivate));
    // if this is a avatar photo - reset all avatars.
    if (response.data.data.is_avatar) dispatch(makeAllAvatarsFalse());

    dispatch(storeSinglePhoto(response.data.data));
    dispatch(updatePhotoUploadProcess(response.data.meta));
  };
};

export const stopPendingPhotoUpload = (isPrivate = false): any => {
  return (dispatch: AppDispatch) => {
    dispatch(endPendingUpload(isPrivate));
    dispatch(clearPendingPhotosUpload());
    dispatch(
      displayFailMessage({
        info: 'Error uploading photo. C Please try again or ',
        linkInfo: 'contact support.',
        url: `${MasterConfig.FULL_URL}/faq/giving-feedback`,
        newWindow: true,
      })
    );
  };
};

export const uploadErrorFlow = (error: any, isPrivate: any, dispatch: any): any => {
  // toast error
  dispatch(endPendingUpload(isPrivate));
  let msg = null;

  if (error.response && error.response.data) {
    if (error.response.data.photo) {
      msg = error.response.data.photo[0];
    } else if (error.response.data.errors) {
      msg = error.response.data.errors[0].detail;
    }
  }

  if (msg) {
    toaster.warn(msg);
  } else {
    dispatch(
      displayFailMessage({
        info: 'Error uploading photo. B Please try again or ',
        linkInfo: 'contact support.',
        url: `${MasterConfig.FULL_URL}/faq/giving-feedback`,
        newWindow: true,
      })
    );
  }
};

export const getPhotoUploadResponse = (
  useDirectUpload: any,
  fileExtension: any,
  isPrivate: any,
  formData: any
): any => {
  let photoUploadResponse;

  if (useDirectUpload === true) {
    photoUploadResponse = sdk.mockProcessPhoto({
      private: isPrivate,
      fileExtension: fileExtension,
      is_cropped: true,
    });
  } else {
    photoUploadResponse = sdk.postNewPhoto(formData);
  }

  return photoUploadResponse;
};

export const addNewPhoto = (formData: any, isPrivate: any): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    // validate 15mb file size
    if (!formData || typeof formData.size === 'undefined' || formData.size >= 15000000) {
      dispatch(endPendingUpload(isPrivate));
      dispatch(
        displayFailMessage({
          info: 'Error uploading photo. A Please try again or ',
          linkInfo: 'contact support.',
          url: `${MasterConfig.FULL_URL}/faq/giving-feedback`,
          newWindow: true,
        })
      );
      return;
    }

    dispatch(pendingUpload(isPrivate));

    const useDirectUpload = getState().profile.direct_s3_upload;
    let photoUploadResponse = null;
    const fileExtension = formData.type.substr(formData.type.lastIndexOf('/') + 1);

    photoUploadResponse = getPhotoUploadResponse(useDirectUpload, fileExtension, isPrivate, formData);

    return photoUploadResponse
      .then((response: any) => {
        const apiResponse = response.data.data;
        const { uploadUrl } = response.data.meta;

        if (useDirectUpload === true) {
          const fileName = apiResponse.path + '.' + fileExtension;
          let newFile = null;
          const photoUrl = apiResponse.urls['820px'] || apiResponse.urls['profile'];

          try {
            newFile = new File([formData], fileName, { type: formData.type });
          } catch (err) {
            console.warn(err);
            newFile = new Blob([formData], { type: formData.type });
            newFile.lastModifiedDate = new Date();
            newFile.lastModified = +newFile.lastModifiedDate;
            newFile.name = fileName;
          }

          sdk.uploadToS3(uploadUrl, newFile).then(
            () => {
              setTimeout(() => {
                sdk
                  .getPhoto(photoUrl)
                  .then(resp => {
                    if (!('data' in resp)) {
                      Sentry.captureException('Photo failed to upload/processed', {
                        extra: {
                          photoPath: photoUrl,
                          data: resp,
                        },
                      });
                      dispatch(stopPendingPhotoUpload(isPrivate));
                      return false;
                    }

                    sdk
                      .processPhoto({
                        private: isPrivate,
                        path: apiResponse.path,
                        is_cropped: true,
                      })
                      .then(processedPhoto => {
                        dispatch(endImageUploadLoader(processedPhoto, isPrivate));
                        dispatch(refreshProfile());
                        dispatch(refreshAccount());
                      });
                  })
                  .catch(error => {
                    Sentry.captureException('Photo failed to upload/processed', {
                      extra: {
                        photoPath: apiResponse.path,
                        data: error,
                      },
                    });
                    dispatch(stopPendingPhotoUpload(isPrivate));
                  });
              }, 6000); // lets wait for the lambda to fully process the image resizing
            },
            () => {
              dispatch(deletePhoto(apiResponse.id));
              dispatch(stopPendingPhotoUpload(isPrivate));
            }
          );
        } else {
          dispatch(endImageUploadLoader(response, isPrivate));
          dispatch(refreshProfile());
          dispatch(refreshAccount());
        }
      })
      .catch((error: any) => {
        uploadErrorFlow(error, isPrivate, dispatch);
      });
  };
};

export const deletePhoto = (photoId: any): any => {
  return async () => {
    try {
      await sdk.deletePhoto(photoId);
    } catch (error) {
      console.log(error);
    }
  };
};

export const makePhotoDefault = (photoId: any, photoKey: any): any => {
  // set default photo.
  return (dispatch: AppDispatch) => {
    // notify store
    dispatch(storeDefaultPhoto(photoKey));
    // notify api
    return sdk.putPhotoAvatar(photoId);
  };
};

export const setNewPrivacy = (photoId: any, photoKey: any): any => {
  // set new photo privacy.
  return (dispatch: AppDispatch) => {
    // notify store
    dispatch(storeNewPrivacy(photoKey));
    // notify api
    return sdk.putPhotoPrivacy(photoId);
  };
};

export const reportUser = (formData: any, navigate: NavigateFunction): any => {
  return (dispatch: AppDispatch) => {
    return sdk
      .reportUser(formData)
      .then(() => {
        removeDiscoverModeAccountsByHashId([formData?.account]);

        // clear errors
        dispatch(clearErrors());
        // redirect user to dashboard
        navigate('/dashboard');
        // notify by toast
        toaster.warn('Profile reported');
      })
      .catch(error => {
        dispatch(storeErrors(error.response.data));
      });
  };
};

export const getReportReasons = (): any => {
  return async (dispatch: AppDispatch) => {
    try {
      //show loader
      dispatch(setProcessing());

      const reasonGroupResponse = await sdk.getReportReasons();

      if (reasonGroupResponse.status === 200 && reasonGroupResponse.data.data.length > 0) {
        dispatch(storeReportReasons(reasonGroupResponse.data.data));
      } else {
        dispatch(
          displayFailMessage({
            info: `Something went wrong, Please try again later`,
          })
        );
      }
    } catch (error) {
      console.warn(error);
      dispatch(
        displayFailMessage({
          info: `Something went wrong, Please try again later`,
        })
      );
    }
  };
};

export const getBlockedProfileList = (
  page = 1,
  messageComp?: { success: React.ReactNode; error: React.ReactNode }
): any => {
  return async (dispatch: AppDispatch, getState: RootState) => {
    try {
      dispatch(storeDismissedUser(null));
      dispatch(setIsFetchingProfileList(true));
      const blockedListResponse = await sdk.getBlockedList(page);

      if (blockedListResponse.status === 200) {
        if (blockedListResponse?.data?.data.length > 0) {
          if (page > 1) {
            const blockedUsers = getState().blocked.blockedItems;
            const userIds = blockedUsers.map((item: any) => item.to_account_hash_id);
            const updatedBlockedList = blockedListResponse.data.data
              .filter((item: any) => !userIds.includes(item.to_account_hash_id))
              .filter((item: any) => item);

            dispatch(storeNewBlockedUser({ data: updatedBlockedList, meta: blockedListResponse.data.meta }));
          } else {
            dispatch(storeNewBlockedUser(blockedListResponse.data));
          }
        }
      } else {
        dispatch(storeNewBlockedUser(null));
        dispatch(
          showNotificationBanner({
            id: uuidv4(),
            message: messageComp?.error,
            type: 'fail',
          })
        );
      }

      dispatch(setIsFetchingProfileList(false));
    } catch (error) {
      console.warn(error);
      dispatch(storeNewBlockedUser(null));
      dispatch(setIsFetchingProfileList(false));
      dispatch(
        showNotificationBanner({
          id: uuidv4(),
          message: messageComp?.error,
          type: 'fail',
        })
      );
    }
  };
};

export const getDismissedProfileList = (
  page = 1,
  messageComp?: { success: React.ReactNode; error: React.ReactNode }
): any => {
  return async (dispatch: AppDispatch, getState: RootState) => {
    try {
      dispatch(storeNewBlockedUser(null));
      dispatch(setIsFetchingProfileList(true));
      const dismissedListResponse = await sdk.getDismissedList(page);

      if (dismissedListResponse.status === 200) {
        if (dismissedListResponse?.data?.data.length > 0) {
          if (page > 1) {
            const dismissedUser = getState().blocked.dismissedProfiles;
            const userIds = dismissedUser.map((item: any) => item.to_account_hash_id);
            const updatedDismissedList = dismissedListResponse.data.data
              .filter((item: any) => !userIds.includes(item.to_account_hash_id))
              .filter((item: any) => item);

            dispatch(storeDismissedUser({ data: updatedDismissedList, meta: dismissedListResponse.data.meta }));
          } else {
            dispatch(storeDismissedUser(dismissedListResponse.data));
          }
        }
      } else {
        dispatch(storeDismissedUser(null));
        dispatch(
          showNotificationBanner({
            message: messageComp?.error,
            type: 'fail',
            id: uuidv4(),
          })
        );
      }

      dispatch(setIsFetchingProfileList(false));
    } catch (error) {
      console.warn(error);
      dispatch(setIsFetchingProfileList(false));
      dispatch(
        showNotificationBanner({
          message: messageComp?.error,
          type: 'fail',
          id: uuidv4(),
        })
      );
    }
  };
};

export const toggleBlockProfile = (
  username: any,
  hashId: any,
  isBlocking = true,
  isFromBlockPage = false,
  navigate: NavigateFunction,
  messageComp?: { success: React.ReactNode; error: React.ReactNode }
): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    const blockType = isBlocking ? 'blocked' : 'unblocked';

    if (isFromBlockPage) {
      dispatch(setDeletedLoader(true));
    }

    return sdk
      .blockUser(hashId)
      .then(response => {
        if (response.status === 201 || response.status === 200) {
          removeDiscoverModeAccountsByHashId([hashId]);

          if (isFromBlockPage) {
            dispatch(toggleBlockInStore(hashId));

            const blockedUsers = getState().blocked.blockedItems;
            const updatedBlockedList = blockedUsers.filter((item: any) => item.to_account_hash_id !== hashId);

            dispatch(updatedBlockedUsers(updatedBlockedList));
            setTimeout(() => {
              dispatch(setDeletedLoader(false));
            }, 700);
            dispatch(
              showNotificationBanner({
                message: messageComp?.success,
                type: 'success',
                id: uuidv4(),
                hideTimeout: 2000,
              })
            );
            navigate(`/profile/${hashId}`);
          } else {
            dispatch(
              displayFailMessage({
                info: `You have ${blockType} ${username}`,
              })
            );
          }
        } else {
          if (isFromBlockPage) {
            dispatch(setDeletedLoader(false));
            dispatch(
              showNotificationBanner({
                message: messageComp?.error,
                type: 'fail',
                id: uuidv4(),
              })
            );
          } else {
            dispatch(
              displayFailMessage({
                info: `Something went wrong, Please try again later`,
              })
            );
          }
        }

        return response;
      })
      .catch(() => {
        if (isFromBlockPage) {
          dispatch(setDeletedLoader(false));
          dispatch(
            showNotificationBanner({
              message: messageComp?.error,
              type: 'fail',
              id: uuidv4(),
            })
          );
        } else {
          dispatch(
            displayFailMessage({
              info: `Something went wrong, Please try again later`,
            })
          );
        }
      });
  };
};

export const deleteDismissProfile = (
  hashId: string,
  messageComp: { success: React.ReactNode; error: React.ReactNode }
): any => {
  return async (dispatch: AppDispatch, getState: RootState) => {
    try {
      dispatch(setDeletedLoader(true));
      const deleteDismissResponse = await sdk.dismissUser(hashId);

      if (deleteDismissResponse.status === 200) {
        const dismissedUser = getState().blocked.dismissedProfiles;
        const updatedDismissedList = dismissedUser.filter((item: any) => item.to_account_hash_id !== hashId);

        dispatch(updateDismissedUser(updatedDismissedList));
        setTimeout(() => {
          dispatch(setDeletedLoader(false));
        }, 700);
        dispatch(
          showNotificationBanner({
            message: messageComp.success,
            type: 'success',
            id: uuidv4(),
            hideTimeout: 2000,
          })
        );
      } else {
        dispatch(
          showNotificationBanner({
            id: uuidv4(),
            message: messageComp.error,
            type: 'fail',
          })
        );
        dispatch(setDeletedLoader(false));
      }
    } catch (error) {
      console.warn(error);
      dispatch(setDeletedLoader(false));
      dispatch(
        showNotificationBanner({
          id: uuidv4(),
          message: messageComp.error,
          type: 'fail',
        })
      );
    }
  };
};

export const clearPendingPhotosUpload = (): { type: string } => {
  return {
    type: 'CLEAR_ALL_UPLOADS',
  };
};
// ---------------

export const clearCredits = (): { type: string } => {
  return {
    type: 'CLEAR_CREDITS',
  };
};

export const storeCredits = (payload: any): ActionReturnType => {
  return {
    type: 'STORE_CREDITS',
    payload,
  };
};

export const isFetchingCredits = (): { type: string } => {
  return {
    type: 'IS_FETCHING_CREDITS',
  };
};

export const refreshCredits = (firstCall = true, per_page = 0): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    let pageToGet = 0;

    if (firstCall) {
      dispatch(clearCredits());
    } else {
      pageToGet = getState().meta.credit_current_page + 1;
    }

    return sdk
      .getCredits(per_page, pageToGet)
      .then(response => {
        dispatch(storeCredits(response.data.data));
        // store pagination
        const page = response.data.meta.pagination;

        dispatch(storeMeta({ credit_current_page: page.current_page, credit_total_pages: page.total_pages }));
        firstCall && dispatch(isFetchingCredits());
      })
      .catch(error => {
        console.log(error);
      });
  };
};

export const toggleDesktopNotificationStatus = (status: any): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    const profile = getState().profile;

    if (status === true && typeof window.dataLayer !== 'undefined') {
      new DataLayer().push({
        event: 'enableDesktopNotification',
        hashId: profile.hash_id,
        submitted_at: profile.profile && profile.profile.data && profile.profile.data.submitted_date,
        completed_at: profile.profile && profile.profile.data && profile.profile.data.completed_date,
        monetized_at: profile.profile && profile.profile.data && profile.profile.data.monetized_date,
      });
    }

    dispatch({
      type: 'SETTINGS_TOGGLE_DESKTOP_NOTIFICATION',
      payload: {
        status,
      },
    });
  };
};

export const updateProfileContent = (hashId: any): any => {
  return (dispatch: AppDispatch) => {
    return sdk
      .getExternalProfile(hashId)
      .then(response => {
        dispatch({
          type: 'REFRESH_EXTERNAL_PROFILE_ADDITIONAL_DATA',
          payload: {
            data: response.data.data,
          },
        });
      })
      .catch(error => {
        console.log(error);
      });
  };
};

export const updateMsgReminder = (): any => {
  return (dispatch: AppDispatch) => {
    return sdk
      .updateStateBasedSettings({ key: 'show_message_reminder', state: true })
      .then(() => {
        dispatch(refreshProfile());
      })
      .catch(error => {
        console.log(error);
      });
  };
};

export const updateShowTutorialOffer = (offerState: any): any => {
  return (dispatch: AppDispatch) => {
    return sdk
      .updateStateBasedSettings({ key: 'show_offer_tutorial', state: offerState })
      .then(() => {
        dispatch(refreshProfile());
      })
      .catch(error => {
        console.log(error);
      });
  };
};

export const updatePhoneNumber = (payload: any, callback: any): any => {
  return (dispatch: AppDispatch) => {
    return sdk
      .updatePhoneNumber(payload)
      .then(() => {
        dispatch(refreshProfile());
        callback();
      })
      .catch(error => {
        dispatch(storeErrors(error.response.data));
      });
  };
};

export const updateProfileNewJoinFlowAction = (formData: any): any => {
  return () => {
    return sdk.updateProfileNewJoinFlow(formData);
  };
};

export const updateAccountConfig = ({ key, state }: { key: string; state: boolean }): any => {
  return (dispatch: AppDispatch) => {
    return sdk
      .updateStateBasedSettings({ key, state })
      .then(() => {
        dispatch(refreshProfile());
      })
      .catch(error => {
        if (error && error.response && error.response.data) {
          dispatch(storeErrors(error.response.data));
        } else {
          console.error('Error response or data is undefined:', error);
        }
      });
  };
};

export const resetHiddenDialogs = (): any => {
  return (dispatch: AppDispatch) => {
    return sdk
      .resetHiddenDialogs()
      .then(() => {
        dispatch(refreshProfile());
      })
      .catch(error => {
        dispatch(storeErrors(error.response.data));
      });
  };
};

export const setShowSelfieVerificationWall = (payload: boolean) => ({
  type: SET_SHOW_SELFIE_VERIFICATION_WALL,
  payload: payload,
});

export const setSVWallByGetSelfieVerified = (payload: boolean) => ({
  type: IS_SV_WALL_BY_GET_SELFIE_VERIFIED,
  payload: payload,
});

export const updateProfileLivenessStatus = () => ({
  type: UPDATE_PROFILE_LIVENESS_STATUS,
});

//faceliveness actions
export const setFaceLivenessVerifyLoader = payload => ({
  type: SET_FACELIVENESS_VERIFY_LOADER,
  payload: payload,
});

export const setFaceLivenessStatus = payload => ({
  type: SET_FACELIVENESS_STATUS,
  payload: payload,
});

export const startSeflieVerificationStep = payload => ({
  type: START_SELFIE_VERIFICATION,
  payload: payload,
});

export const skipShowSelfieVerificationWallCheck = () => ({
  type: SKIP_SELFIE_VERIFICATION_WALL_CHECK,
});

//liveness submit result
export const facelivenessSubmitAction = (hashId: string, result: any, origin: string): any => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setFaceLivenessVerifyLoader(true));

      const verifyResponse = await sdk.postSelfieVerification(hashId, result);

      const formData = {
        callback_data: {
          user_uid: hashId,
        },
      };

      if (verifyResponse.data && verifyResponse.status === 'success') {
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        formData.results = { ...verifyResponse.data, origin };
      } else {
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        formData.results = {
          recommendation: 'ERROR',
          origin,
        };
      }

      const livenessResponse = await sdk.updateFacelivenessResult(hashId, formData);

      if (livenessResponse.status === 200 && !isEmpty(livenessResponse) && !isEmpty(livenessResponse.data.data)) {
        const livenessStatusObject = {
          recommendation: livenessResponse.data.data.recommendation,
          attempts: livenessResponse.data.data.attempts,
          status: livenessResponse.data.data.status,
          is_last_attempt: livenessResponse.data.data.is_last_attempt,
        };

        dispatch(setFaceLivenessStatus(livenessStatusObject));
        dispatch(setFaceLivenessVerifyLoader(false));
      } else {
        toaster.error("Something wen't wrong, Please try again later.");
        dispatch(setShowSelfieVerificationWall(false));
        dispatch(setFaceLivenessVerifyLoader(false));
      }
    } catch (error) {
      console.warn('error', error);
      const errorDetail = (error?.response?.data?.errors && error.response.data.errors[0]) || null;

      dispatch(setFaceLivenessVerifyLoader(false));

      if (error.status == 401 && [40125004, 40125005, 40125006].includes(errorDetail.code)) {
        dispatch(
          setFaceLivenessStatus({
            recommendation: 'max_attempt',
            message: errorDetail.message,
            code: errorDetail.code,
          })
        );
        return;
      }

      toaster.error("Something wen't wrong, Please try again later.");
      dispatch(setShowSelfieVerificationWall(false));
    }
  };
};

//faceliveness skip action
export const facelivenessSkipAction = (hashId: string): any => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setFaceLivenessVerifyLoader(true));
      const skipResponse = await sdk.selfieVerificationSkip(hashId);

      if (skipResponse.status !== 200) {
        toaster.error("Something wen't wrong, Please try again later.");
      }

      dispatch(setFaceLivenessVerifyLoader(false));
    } catch (error) {
      console.warn('error', error);
      dispatch(setFaceLivenessVerifyLoader(false));
      toaster.error("Something wen't wrong, Please try again later.");
    }
  };
};

export const unlinkPhoneNumber = (payload: any, callback: any): any => {
  return (dispatch: AppDispatch) => {
    return sdk
      .unlinkPhoneNumber(payload)
      .then(() => {
        dispatch(refreshProfile());
        callback();
      })
      .catch(error => {
        dispatch(storeErrors(error.response.data));
      });
  };
};

export const updateUnreadMessagesCount = (count?: number): any => {
  return async (dispatch: AppDispatch) => {
    try {
      let unreadMessages = count;

      if (typeof unreadMessages !== 'number') {
        unreadMessages = await sdk.getUnreadCount();
      }

      dispatch({
        type: 'UPDATE_UNREAD_MESSAGES_COUNT',
        payload: {
          count: unreadMessages,
        },
      });
    } catch (error) {
      console.log(error);
    }
  };
};
